/* eslint-disable react/jsx-key */
import { useTranslation } from 'react-i18next';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import AnalyticsSubPage from '@customer/templates/health-sub-page/AnalyticsSubPage';
import { useCallback, useMemo } from 'react';
import {
  AssetBasicModel,
  AssetListScenarios,
  HealthReportingAssetsFilters,
  ReportingStatus,
} from '@customer/store/apis/assetsApi';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import Metric, { MetricSize } from '@customer/components/data-display/metric/Metric';
import useAnalyticsSubPage from '@customer/templates/health-sub-page/useAnalyticsSubPage';
import { AssetDetailsAccordion, AssetSidePanelTab } from '@customer/store/slices/globalSlice';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { HealthScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import BarChartByDeviceType from '@customer/components/charts/by-device-type/BarChartByDeviceType';
import InsightsCard from '@customer/components/data-display/insights-card/InsightsCard';
import StackedBar from '@customer/components/charts/stacked-bar/StackedBar';
import {
  columnDefAssetGroup,
  columnDefAssetName,
  columnDefConfigGroupName,
  columnDefDeviceType,
  columnDefReportingStatus,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef } from 'ag-grid-community';
import { FilterGroup } from '@customer/filters/types';
import BarChartByAssetGroup from '@customer/components/charts/by-asset-group/BarChartByAssetGroup';
import BarChartByConfigGroup from '@customer/components/charts/by-config-group/BarChartByConfigGroup';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';

const AssetGridSliceKey = 'AssetsHealthReporting';
const filterGroup = FilterGroup.HealthReporting;

export function Reporting() {
  const { t } = useTranslation();

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<AssetBasicModel>();

  const columnDefs = useMemo<ColDef<AssetBasicModel>[]>(
    () => [
      { ...columnDefAssetName(t), maxWidth: 150 },
      { ...columnDefDeviceType(t), maxWidth: 110 },
      { ...columnDefAssetGroup(t) },
      { ...columnDefConfigGroupName(t), maxWidth: 250 },
      {
        field: 'ts',
        headerName: t('LastReportedTitleCase'),
        maxWidth: 200,
        ...absoluteTimeColDef,
      },
      { ...columnDefReportingStatus(t), maxWidth: 150 },
    ],
    [t, absoluteTimeColDef],
  );

  const { assets, hasData, noDataDescription, countAll, countFilter, onRowClick } =
    useAnalyticsSubPage<HealthReportingAssetsFilters>({
      scenario: AssetListScenarios.HealthReporting,
      filterGroup,
      sidePanelTab: AssetSidePanelTab.Details,
      sidePanelDefaultAccordion: AssetDetailsAccordion.ReportingStatus,
    });

  const numAssetsReportedBad = assets.filter(asset => asset.stsRep === ReportingStatus.Bad).length;
  const numAssetsReportedModerate = assets.filter(
    asset => asset.stsRep === ReportingStatus.Moderate,
  ).length;
  const numAssetsNotReporting = numAssetsReportedBad + numAssetsReportedModerate;

  const getDataSetLabel = useCallback((label: string) => t(`ReportingStatus${label}`), [t]);

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByAssetGroup'),
        ChartComponent: BarChartByAssetGroup<HealthScenarioQueryParams>,
        scenario: StatsScenario.ReportingByAssetGroup,
      },
      {
        title: t('ByDeviceType'),
        ChartComponent: BarChartByDeviceType<HealthScenarioQueryParams>,
        scenario: StatsScenario.ReportingByDeviceType,
      },
      {
        title: t('ByConfigurationGroup'),
        ChartComponent: BarChartByConfigGroup<HealthScenarioQueryParams>,
        scenario: StatsScenario.ReportingByConfigGroup,
      },
    ],
    [t],
  );

  return (
    <AnalyticsSubPage
      countAll={countAll}
      countFilter={countFilter}
      filters={filterGroup}
      content={
        <>
          <InsightsCard
            sections={[
              <Metric
                dataPrimary={numAssetsNotReporting}
                dataSecondary={countFilter}
                isFraction
                label={
                  <>
                    <span>{t('AssetsNotReporting')}</span>
                    <StackedBar
                      total={countFilter}
                      portions={[numAssetsReportedBad, numAssetsReportedModerate]}
                      colorScheme={ChartColorScheme.RedYellowGrey}
                    />
                  </>
                }
                size={MetricSize.XL}
              />,
              <Metric
                dataPrimary={numAssetsReportedBad}
                dataSecondary={countFilter}
                isFraction
                status={BadgeStatus.Danger}
                label={t('MetricAssetsConnectedBad')}
                size={MetricSize.XL}
              />,
              <Metric
                dataPrimary={numAssetsReportedModerate}
                dataSecondary={countFilter}
                isFraction
                status={BadgeStatus.Caution}
                label={t('MetricAssetsConnectedModerate')}
                size={MetricSize.XL}
              />,
            ]}
          />

          <ChartScenarios
            title={t('ReportingStatus')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={filterGroup}
            colorScheme={ChartColorScheme.RedYellowGrey}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="AssetsWithCount"
          />

          <AnalyticsTable
            title={t('AssetsNotReporting')}
            metrics={
              <>
                <Metric
                  dataPrimary={numAssetsReportedBad}
                  dataSecondary={countFilter}
                  isFraction
                  status={BadgeStatus.Danger}
                />
                <Metric
                  dataPrimary={numAssetsReportedModerate}
                  dataSecondary={countFilter}
                  isFraction
                  status={BadgeStatus.Caution}
                />
              </>
            }
            hasData={hasData}
            noDataDescription={noDataDescription}
          >
            <GridControlled<AssetBasicModel>
              sliceKey={AssetGridSliceKey}
              rowData={assets}
              columnDefs={columnDefs}
              noRowsMessage={t('No assets')}
              onRowClick={onRowClick}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default Reporting;
